import { computed, unref, Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Note from "@/services/note";
import { GetNote } from "@/dtos/Note.dto";

export interface NotesParams {
  contactId: string;
  organizationId: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useNotes(params: Ref<NotesParams | null> | NotesParams | null) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["notes", "disabled"];
    return ["notes", p.contactId, p.organizationId];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.contactId && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const queryParams: GetNote & { contactId?: string } = {
        contactId: p.contactId,
        organizationId: p.organizationId,
      };

      const result = await Note.getNotes(queryParams);

      if (result.data.status) {
        return result.data.data;
      }

      throw new Error(result.data.msg || "Failed to load notes");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
